/**
 * Wer außer mir an dieser Reise arbeitet — die Regeln der Reisezeile.
 *
 * ## Warum das nicht in `TripList.svelte` steht
 *
 * Aus demselben Grund wie die Wegerkennung in `wege.ts`: In einer Komponente
 * wäre es nur mit Browser prüfbar, und genau hier lohnt die Prüfung. Die
 * Entscheidung „eigene oder fremde Reise" schaltet in der Liste **Knöpfe** —
 * Papierkorb und Kopieren verschwinden an einer fremden Zeile, weil der Server
 * sie ohnehin mit 404 beantwortet. Fällt diese Unterscheidung falsch aus,
 * sieht man das der Zeile nicht an: Sie sieht richtig aus und der Knopf lügt.
 *
 * ## Der Server entscheidet, nicht wir
 *
 * `mitplaner_von` setzt das Backend genau dann, wenn die Reise jemand anderem
 * gehört (`db.trips`: `WHERE ub.id = t.user_id AND t.user_id <> %s`). Die
 * Oberfläche vergleicht deshalb **nichts** — sie kennt ihre eigene Kontonummer
 * gar nicht und soll sie hier auch nicht brauchen. Ein Vergleich am Client
 * wäre eine zweite Antwort auf dieselbe Frage, und zwei Antworten gehen
 * auseinander.
 */

/** Das Wenige, was die Zeile über die Reise wissen muss. */
export interface Reisebeteiligung {
  /** Anzeigename des Besitzers — nur bei fremden Reisen gesetzt. */
  mitplaner_von?: string | null;
  /** Anzeigenamen der Mitplaner — nur bei eigenen Reisen gefüllt. */
  mitplaner?: string[] | null;
}

/**
 * Gehört diese Reise jemand anderem?
 *
 * Ein leerer Name zählt **nicht** als fremd: Käme aus der Datenbank ein Konto
 * ohne Anzeigename, stünde in der Zeile „von " und die Reise verlöre ihren
 * Papierkorb — an einer Reise, die dem Betrachter gehört. Lieber die Zeile
 * ohne Zusatz als eine Reise, die sich nicht mehr löschen lässt.
 */
export function istFremd(t: Reisebeteiligung): boolean {
  return Boolean(t.mitplaner_von?.trim());
}

/**
 * Die dritte Zeile: mit wem diese Reise geteilt ist.
 *
 * Zwei Richtungen, ein Ort. „von Andrea" bei einer fremden Reise, „mit Thomas"
 * bei einer eigenen mit Mitplanern — und `null`, wenn beides nicht zutrifft.
 * Eine Zeile „nur du" an jeder Reise wäre eine Angabe, die nie jemand sucht,
 * und sie stünde bei den allermeisten.
 *
 * `mitplaner_von` schlägt `mitplaner`, falls je beides käme: Wessen Reise das
 * ist, ist die wichtigere der beiden Auskünfte — sie entscheidet, was man hier
 * tun darf.
 *
 * Namenlose Einträge fallen heraus, statt als Lücke mitzulaufen: „mit , Thomas"
 * sieht aus wie ein Fehler in der Liste und ist einer.
 */
export function mitwirkende(t: Reisebeteiligung): string | null {
  const besitzer = t.mitplaner_von?.trim();
  if (besitzer) return `von ${besitzer}`;
  const namen = (t.mitplaner ?? []).map((n) => n?.trim()).filter((n): n is string => !!n);
  if (!namen.length) return null;
  return `mit ${namen.join(', ')}`;
}
